import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  orders as ordersApi,
  hotels,
  calls as callsApi,
  users as usersApi,
} from "../api/endpoints";
import type {
  CallType,
  ServiceLevel,
  PaymentType,
  HotelProperty,
  Language,
  User,
  CallSpec,
  PurchaseOrderIn,
} from "../types";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CallRow extends CallSpec {
  /** Client-side key for React list rendering */
  _key: string;
  /** Resolved cost from the rates matrix; null = not yet resolved */
  _cost: number | null;
  /** Display-only: property name after selection */
  _propertyLabel: string;
  /** Display-only: management company from selected property */
  _hmcLabel: string;
  /** Scheduled month (1-12) for grouping; derived from scheduled_call_date */
  _scheduledMonth: number | null;
}

interface OrderSetup {
  requestor_id: number;
  invoice_id: string;
  po_number: string;
  payment_type_id: number | null;
  invoice_notes: string;
}

type ValidationErrors = Record<string, string>;

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

let _keyCounter = 0;
function nextKey(): string {
  return `call_${++_keyCounter}_${Date.now()}`;
}

function blankCall(): CallRow {
  return {
    _key: nextKey(),
    _cost: null,
    _propertyLabel: "",
    _hmcLabel: "",
    _scheduledMonth: null,
    property_id: 0,
    sales_staff_email: "",
    call_type_id: 0,
    service_level_id: 0,
    language_id: undefined,
    scenario: "",
    earliest_call_date: "",
    scheduled_call_date: "",
  };
}

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function monthFromDate(dateStr: string | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d.getMonth() + 1;
}

/* ------------------------------------------------------------------ */
/*  Searchable property dropdown                                       */
/* ------------------------------------------------------------------ */

interface PropertySearchProps {
  properties: HotelProperty[];
  value: number;
  onChange: (property: HotelProperty | null) => void;
  error?: string;
}

function PropertySearch({ properties, value, onChange, error }: PropertySearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selected = properties.find((p) => p.id === value);

  const filtered = useMemo(() => {
    if (!query.trim()) return properties.slice(0, 50);
    const q = query.toLowerCase();
    return properties
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.hotel_code.toLowerCase().includes(q) ||
          (p.city && p.city.toLowerCase().includes(q)) ||
          (p.state && p.state.toLowerCase().includes(q)) ||
          (p.management_company?.name && p.management_company.name.toLowerCase().includes(q)),
      )
      .slice(0, 50);
  }, [query, properties]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      {selected && !open ? (
        <button
          type="button"
          className="input-shell w-full text-left truncate"
          onClick={() => {
            setOpen(true);
            setQuery("");
          }}
        >
          <span className="font-semibold">{selected.hotel_code}</span>
          <span className="mx-1 text-slate-400">-</span>
          {selected.name}
        </button>
      ) : (
        <input
          type="text"
          placeholder="Search by name, code, city, or HMC..."
          className={`input-shell w-full ${error ? "border-red-400 ring-1 ring-red-300" : ""}`}
          value={query}
          autoFocus={open}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
      )}
      {open && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg">
          {filtered.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-400">No properties match your search</div>
          )}
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`flex w-full items-start gap-2 px-4 py-2.5 text-left text-sm hover:bg-indigo-50 transition-colors ${
                p.id === value ? "bg-indigo-50 font-semibold" : ""
              }`}
              onClick={() => {
                onChange(p);
                setOpen(false);
                setQuery("");
              }}
            >
              <span className="shrink-0 font-mono text-xs text-indigo-600">{p.hotel_code}</span>
              <span className="min-w-0">
                <span className="block truncate text-slate-900">{p.name}</span>
                {p.management_company && (
                  <span className="block truncate text-xs text-slate-400">
                    {p.management_company.name}
                    {p.city ? ` \u00B7 ${p.city}, ${p.state}` : ""}
                  </span>
                )}
              </span>
            </button>
          ))}
          {filtered.length === 50 && (
            <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
              Showing first 50 results. Narrow your search to find more.
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step indicator                                                     */
/* ------------------------------------------------------------------ */

const STEPS = ["Order Setup", "Add Calls", "Review & Submit"] as const;

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {STEPS.map((label, i) => {
        const step = i + 1;
        const done = step < current;
        const active = step === current;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <div
                className={`hidden h-px w-8 sm:block ${
                  done ? "bg-indigo-500" : "bg-slate-200"
                }`}
              />
            )}
            <div className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                  done
                    ? "bg-indigo-500 text-white"
                    : active
                    ? "bg-indigo-600 text-white ring-4 ring-indigo-100"
                    : "bg-slate-100 text-slate-400"
                }`}
              >
                {done ? "\u2713" : step}
              </span>
              <span
                className={`hidden text-sm font-medium sm:inline ${
                  active ? "text-slate-900" : "text-slate-400"
                }`}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

interface Props {
  user: User;
}

export default function PurchaseCallPage({ user }: Props) {
  const navigate = useNavigate();

  /* ---- Reference data ---- */
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [callTypes, setCallTypes] = useState<CallType[]>([]);
  const [serviceLevels, setServiceLevels] = useState<ServiceLevel[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [rates, setRates] = useState<{ call_type_id: number; service_level_id: number; rate: number }[]>([]);
  const [refLoading, setRefLoading] = useState(true);

  /* ---- Wizard state ---- */
  const [step, setStep] = useState(1);

  /* ---- Step 1 state ---- */
  const [setup, setSetup] = useState<OrderSetup>({
    requestor_id: user.id,
    invoice_id: "",
    po_number: "",
    payment_type_id: null,
    invoice_notes: "",
  });

  /* ---- Step 2 state ---- */
  const [callRows, setCallRows] = useState<CallRow[]>([blankCall()]);

  /* ---- Submission state ---- */
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<{ order_id: number; calls_created: number; total_amount: number } | null>(null);

  /* ---- Validation ---- */
  const [stepErrors, setStepErrors] = useState<ValidationErrors>({});
  const [callErrors, setCallErrors] = useState<Record<number, ValidationErrors>>({});

  /* ---------------------------------------------------------------- */
  /*  Load reference data                                              */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    Promise.all([
      hotels.properties.list("limit=5000"),
      callsApi.types(),
      callsApi.serviceLevels(),
      ordersApi.paymentTypes(),
      usersApi.languages(),
      callsApi.rates(),
    ])
      .then(([propRes, types, levels, ptypes, langs, ratesData]) => {
        setProperties((propRes as any).items ?? propRes);
        setCallTypes(types);
        setServiceLevels(levels);
        setPaymentTypes(ptypes);
        setLanguages(langs);
        setRates(ratesData);
      })
      .catch(() => {})
      .finally(() => setRefLoading(false));
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Rate lookup                                                      */
  /* ---------------------------------------------------------------- */

  const rateMap = useMemo(() => {
    const m = new Map<string, number>();
    rates.forEach((r) => m.set(`${r.call_type_id}_${r.service_level_id}`, r.rate));
    return m;
  }, [rates]);

  const lookupRate = useCallback(
    (callTypeId: number, serviceLevelId: number): number | null => {
      if (!callTypeId || !serviceLevelId) return null;
      return rateMap.get(`${callTypeId}_${serviceLevelId}`) ?? null;
    },
    [rateMap],
  );

  /* ---------------------------------------------------------------- */
  /*  Call row helpers                                                  */
  /* ---------------------------------------------------------------- */

  const updateCallRow = useCallback(
    (index: number, patch: Partial<CallRow>) => {
      setCallRows((prev) => {
        const next = [...prev];
        const row = { ...next[index], ...patch };
        // Auto-resolve cost
        const ct = patch.call_type_id ?? row.call_type_id;
        const sl = patch.service_level_id ?? row.service_level_id;
        row._cost = lookupRate(ct, sl);
        // Auto-resolve scheduled month
        const scd = patch.scheduled_call_date ?? row.scheduled_call_date;
        row._scheduledMonth = monthFromDate(scd);
        next[index] = row;
        return next;
      });
      // Clear validation errors for this field
      setCallErrors((prev) => {
        const copy = { ...prev };
        if (copy[index]) {
          const cleared = { ...copy[index] };
          Object.keys(patch).forEach((k) => delete cleared[k]);
          copy[index] = cleared;
        }
        return copy;
      });
    },
    [lookupRate],
  );

  const addCallRow = useCallback(() => {
    setCallRows((prev) => [...prev, blankCall()]);
  }, []);

  const removeCallRow = useCallback((index: number) => {
    setCallRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
    setCallErrors((prev) => {
      const copy = { ...prev };
      delete copy[index];
      // Re-index
      const reindexed: Record<number, ValidationErrors> = {};
      Object.keys(copy).forEach((k) => {
        const old = Number(k);
        reindexed[old > index ? old - 1 : old] = copy[old];
      });
      return reindexed;
    });
  }, []);

  const duplicateCallRow = useCallback((index: number) => {
    setCallRows((prev) => {
      const source = prev[index];
      const dupe: CallRow = { ...source, _key: nextKey() };
      const next = [...prev];
      next.splice(index + 1, 0, dupe);
      return next;
    });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Totals                                                           */
  /* ---------------------------------------------------------------- */

  const totalCost = useMemo(
    () => callRows.reduce((sum, r) => sum + (r._cost ?? 0), 0),
    [callRows],
  );

  const resolvedCount = useMemo(
    () => callRows.filter((r) => r._cost !== null).length,
    [callRows],
  );

  /* ---------------------------------------------------------------- */
  /*  Validation                                                       */
  /* ---------------------------------------------------------------- */

  function validateStep1(): boolean {
    const errors: ValidationErrors = {};
    if (!setup.requestor_id) errors.requestor_id = "Requestor is required.";
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function validateStep2(): boolean {
    const allErrors: Record<number, ValidationErrors> = {};
    let valid = true;

    if (callRows.length === 0) {
      setStepErrors({ calls: "At least one call is required." });
      return false;
    }
    setStepErrors({});

    callRows.forEach((row, i) => {
      const errs: ValidationErrors = {};
      if (!row.property_id) errs.property_id = "Property is required.";
      if (!row.call_type_id) errs.call_type_id = "Call type is required.";
      if (!row.service_level_id) errs.service_level_id = "Service level is required.";
      if (Object.keys(errs).length > 0) {
        allErrors[i] = errs;
        valid = false;
      }
    });

    setCallErrors(allErrors);
    return valid;
  }

  /* ---------------------------------------------------------------- */
  /*  Navigation                                                       */
  /* ---------------------------------------------------------------- */

  function goNext() {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  }

  function goBack() {
    if (step > 1) setStep(step - 1);
  }

  /* ---------------------------------------------------------------- */
  /*  Submit                                                           */
  /* ---------------------------------------------------------------- */

  async function handleSubmit() {
    if (!validateStep2()) {
      setStep(2);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    const payload: PurchaseOrderIn = {
      requestor_id: setup.requestor_id,
      status_id: 1,
      payment_type_id: setup.payment_type_id ?? undefined,
      invoice_id: setup.invoice_id || undefined,
      po_number: setup.po_number || undefined,
      invoice_notes: setup.invoice_notes || undefined,
      calls: callRows.map((row) => ({
        property_id: row.property_id,
        sales_staff_email: row.sales_staff_email || undefined,
        call_type_id: row.call_type_id,
        service_level_id: row.service_level_id,
        language_id: row.language_id || undefined,
        scenario: row.scenario || undefined,
        earliest_call_date: row.earliest_call_date || undefined,
        scheduled_call_date: row.scheduled_call_date || undefined,
      })) as CallSpec[],
    };

    try {
      const result = await ordersApi.purchase(payload);
      setSubmitResult(result);
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred. Please try again.";
      setSubmitError(typeof message === "string" ? message : JSON.stringify(message));
    } finally {
      setSubmitting(false);
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Success screen                                                   */
  /* ---------------------------------------------------------------- */

  if (submitResult) {
    return (
      <div className="mx-auto max-w-xl space-y-6 py-12">
        <section className="surface-panel rise-in p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
            <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Order Created</h1>
          <p className="mt-2 text-sm text-slate-500">
            Your purchase order has been submitted successfully.
          </p>

          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="kpi-card">
              <div className="label-caps">Order</div>
              <div className="mt-1 text-xl font-semibold text-slate-900">#{submitResult.order_id}</div>
            </div>
            <div className="kpi-card">
              <div className="label-caps">Calls</div>
              <div className="mt-1 text-xl font-semibold text-slate-900">{submitResult.calls_created}</div>
            </div>
            <div className="kpi-card">
              <div className="label-caps">Total</div>
              <div className="mt-1 text-xl font-semibold text-slate-900">
                {formatCurrency(submitResult.total_amount)}
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            <button className="button-primary" onClick={() => navigate("/orders")}>
              View Orders
            </button>
            <button
              className="button-ghost"
              onClick={() => {
                setSubmitResult(null);
                setCallRows([blankCall()]);
                setSetup({ requestor_id: user.id, invoice_id: "", po_number: "", payment_type_id: null, invoice_notes: "" });
                setStep(1);
              }}
            >
              Create Another
            </button>
          </div>
        </section>
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /*  Loading state                                                    */
  /* ---------------------------------------------------------------- */

  if (refLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="surface-panel rise-in px-8 py-10 text-center">
          <div className="eyebrow">Loading</div>
          <p className="mt-2 text-sm text-slate-500">Fetching properties, call types, and rates...</p>
        </div>
      </div>
    );
  }

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="surface-panel rise-in p-5 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Purchase Calls</h1>
            <p className="mt-1 text-sm text-slate-500">
              Create a new order with one or many mystery-shopping calls.
            </p>
          </div>
          <StepIndicator current={step} />
        </div>
        {/* Progress bar */}
        <div className="progress-track mt-5">
          <div
            className="progress-fill"
            style={{ width: `${(step / STEPS.length) * 100}%` }}
          />
        </div>
      </section>

      {/* Global errors */}
      {submitError && (
        <section className="rise-in rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <strong>Submission failed:</strong> {submitError}
        </section>
      )}

      {stepErrors.calls && (
        <section className="rise-in rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {stepErrors.calls}
        </section>
      )}

      {/* ============================================================ */}
      {/*  STEP 1 — Order Setup                                        */}
      {/* ============================================================ */}
      {step === 1 && (
        <section className="surface-panel rise-in delay-1 p-5 md:p-6">
          <div className="eyebrow mb-4">Step 1 of 3</div>
          <h2 className="text-lg font-semibold text-slate-900">Order Setup</h2>
          <p className="mt-1 text-sm text-slate-500">
            Define the invoice details and payment method for this purchase order.
          </p>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            {/* Requestor (read-only, auto-populated) */}
            <div>
              <label className="label-caps mb-1.5 block">Requestor</label>
              <input
                type="text"
                className="input-shell w-full bg-slate-50 cursor-not-allowed"
                value={`${user.first_name} ${user.last_name} (${user.email})`}
                readOnly
              />
              {stepErrors.requestor_id && (
                <p className="mt-1 text-xs text-red-600">{stepErrors.requestor_id}</p>
              )}
            </div>

            {/* Invoice ID */}
            <div>
              <label className="label-caps mb-1.5 block">Invoice ID</label>
              <input
                type="text"
                className="input-shell w-full"
                placeholder="e.g. INV-2026-001"
                value={setup.invoice_id}
                onChange={(e) => setSetup((s) => ({ ...s, invoice_id: e.target.value }))}
              />
            </div>

            {/* PO Number */}
            <div>
              <label className="label-caps mb-1.5 block">PO Number</label>
              <input
                type="text"
                className="input-shell w-full"
                placeholder="e.g. PO-12345"
                value={setup.po_number}
                onChange={(e) => setSetup((s) => ({ ...s, po_number: e.target.value }))}
              />
            </div>

            {/* Payment Type */}
            <div>
              <label className="label-caps mb-1.5 block">Payment Type</label>
              <select
                className="select-shell w-full"
                value={setup.payment_type_id ?? ""}
                onChange={(e) =>
                  setSetup((s) => ({
                    ...s,
                    payment_type_id: e.target.value ? Number(e.target.value) : null,
                  }))
                }
              >
                <option value="">-- Select --</option>
                {paymentTypes.map((pt) => (
                  <option key={pt.id} value={pt.id}>
                    {pt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Invoice notes - full width */}
            <div className="sm:col-span-2">
              <label className="label-caps mb-1.5 block">Invoice Notes</label>
              <textarea
                className="textarea-shell w-full"
                rows={3}
                placeholder="Optional notes for the invoice..."
                value={setup.invoice_notes}
                onChange={(e) => setSetup((s) => ({ ...s, invoice_notes: e.target.value }))}
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button className="button-primary" onClick={goNext}>
              Continue to Add Calls
            </button>
          </div>
        </section>
      )}

      {/* ============================================================ */}
      {/*  STEP 2 — Add Calls                                          */}
      {/* ============================================================ */}
      {step === 2 && (
        <>
          {/* Summary bar */}
          <section className="surface-muted rise-in delay-1 p-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="badge-pill bg-indigo-100 text-indigo-800 font-semibold">
                {callRows.length} call{callRows.length !== 1 ? "s" : ""}
              </span>
              <span className="text-slate-500">
                {resolvedCount} of {callRows.length} priced
              </span>
              <span className="ml-auto text-lg font-semibold text-slate-900">
                Running total: {formatCurrency(totalCost)}
              </span>
            </div>
          </section>

          {/* Call rows */}
          {callRows.map((row, idx) => {
            const errs = callErrors[idx] ?? {};
            return (
              <section
                key={row._key}
                className="surface-panel rise-in p-5 md:p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="eyebrow">Call {idx + 1}</span>
                    {row._cost !== null && (
                      <span className="badge-pill bg-emerald-100 text-emerald-800 font-semibold">
                        {formatCurrency(row._cost)}
                      </span>
                    )}
                    {row._hmcLabel && (
                      <span className="badge-pill bg-slate-100 text-slate-600 text-xs">
                        {row._hmcLabel}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      className="button-ghost text-xs"
                      title="Duplicate call"
                      onClick={() => duplicateCallRow(idx)}
                    >
                      Duplicate
                    </button>
                    {callRows.length > 1 && (
                      <button
                        type="button"
                        className="button-danger text-xs"
                        title="Remove call"
                        onClick={() => removeCallRow(idx)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {/* Property */}
                  <div className="sm:col-span-2 lg:col-span-3">
                    <label className="label-caps mb-1.5 block">Property</label>
                    <PropertySearch
                      properties={properties}
                      value={row.property_id}
                      error={errs.property_id}
                      onChange={(p) => {
                        if (p) {
                          updateCallRow(idx, {
                            property_id: p.id,
                            _propertyLabel: `${p.hotel_code} - ${p.name}`,
                            _hmcLabel: p.management_company?.name ?? "",
                            sales_staff_email: row.sales_staff_email || p.dos_email || "",
                          });
                        } else {
                          updateCallRow(idx, {
                            property_id: 0,
                            _propertyLabel: "",
                            _hmcLabel: "",
                          });
                        }
                      }}
                    />
                  </div>

                  {/* Sales Staff Email */}
                  <div>
                    <label className="label-caps mb-1.5 block">Sales Staff Email</label>
                    <input
                      type="email"
                      className="input-shell w-full"
                      placeholder="name@hotel.com"
                      value={row.sales_staff_email || ""}
                      onChange={(e) => updateCallRow(idx, { sales_staff_email: e.target.value })}
                    />
                  </div>

                  {/* Call Type */}
                  <div>
                    <label className="label-caps mb-1.5 block">Call Type</label>
                    <select
                      className={`select-shell w-full ${errs.call_type_id ? "border-red-400 ring-1 ring-red-300" : ""}`}
                      value={row.call_type_id || ""}
                      onChange={(e) =>
                        updateCallRow(idx, { call_type_id: Number(e.target.value) })
                      }
                    >
                      <option value="">-- Select --</option>
                      {callTypes.map((ct) => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name}
                        </option>
                      ))}
                    </select>
                    {errs.call_type_id && (
                      <p className="mt-1 text-xs text-red-600">{errs.call_type_id}</p>
                    )}
                  </div>

                  {/* Service Level */}
                  <div>
                    <label className="label-caps mb-1.5 block">Service Level</label>
                    <select
                      className={`select-shell w-full ${errs.service_level_id ? "border-red-400 ring-1 ring-red-300" : ""}`}
                      value={row.service_level_id || ""}
                      onChange={(e) =>
                        updateCallRow(idx, { service_level_id: Number(e.target.value) })
                      }
                    >
                      <option value="">-- Select --</option>
                      {serviceLevels.map((sl) => (
                        <option key={sl.id} value={sl.id}>
                          {sl.name}
                        </option>
                      ))}
                    </select>
                    {errs.service_level_id && (
                      <p className="mt-1 text-xs text-red-600">{errs.service_level_id}</p>
                    )}
                  </div>

                  {/* Language */}
                  <div>
                    <label className="label-caps mb-1.5 block">Language</label>
                    <select
                      className="select-shell w-full"
                      value={row.language_id ?? ""}
                      onChange={(e) =>
                        updateCallRow(idx, {
                          language_id: e.target.value ? Number(e.target.value) : undefined,
                        })
                      }
                    >
                      <option value="">-- Default --</option>
                      {languages.map((lang) => (
                        <option key={lang.id} value={lang.id}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Scenario */}
                  <div>
                    <label className="label-caps mb-1.5 block">Scenario</label>
                    <input
                      type="text"
                      className="input-shell w-full"
                      placeholder="e.g. Meeting for 50"
                      value={row.scenario || ""}
                      onChange={(e) => updateCallRow(idx, { scenario: e.target.value })}
                    />
                  </div>

                  {/* Earliest Date */}
                  <div>
                    <label className="label-caps mb-1.5 block">Earliest Call Date</label>
                    <input
                      type="date"
                      className="input-shell w-full"
                      value={row.earliest_call_date || ""}
                      onChange={(e) => updateCallRow(idx, { earliest_call_date: e.target.value })}
                    />
                  </div>

                  {/* Scheduled Date */}
                  <div>
                    <label className="label-caps mb-1.5 block">Scheduled Call Date</label>
                    <input
                      type="date"
                      className="input-shell w-full"
                      value={row.scheduled_call_date || ""}
                      onChange={(e) => updateCallRow(idx, { scheduled_call_date: e.target.value })}
                    />
                  </div>

                  {/* Scheduled Month (derived, read-only) */}
                  <div>
                    <label className="label-caps mb-1.5 block">Scheduled Month</label>
                    <input
                      type="text"
                      className="input-shell w-full bg-slate-50 cursor-not-allowed"
                      readOnly
                      value={
                        row._scheduledMonth
                          ? new Date(0, row._scheduledMonth - 1).toLocaleString("en-US", { month: "long" })
                          : "Set via scheduled date"
                      }
                    />
                  </div>
                </div>
              </section>
            );
          })}

          {/* Add call + nav */}
          <section className="surface-muted rise-in p-4">
            <div className="flex flex-wrap items-center gap-3">
              <button className="button-accent" onClick={addCallRow}>
                + Add Another Call
              </button>
              <span className="text-xs text-slate-400">
                {callRows.length} call{callRows.length !== 1 ? "s" : ""} in batch
              </span>
              <div className="ml-auto flex gap-3">
                <button className="button-ghost" onClick={goBack}>
                  Back
                </button>
                <button className="button-primary" onClick={goNext}>
                  Review Order
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ============================================================ */}
      {/*  STEP 3 — Review & Submit                                    */}
      {/* ============================================================ */}
      {step === 3 && (
        <>
          {/* Order summary */}
          <section className="surface-panel rise-in delay-1 p-5 md:p-6">
            <div className="eyebrow mb-4">Step 3 of 3</div>
            <h2 className="text-lg font-semibold text-slate-900">Review & Submit</h2>
            <p className="mt-1 text-sm text-slate-500">
              Verify all details before submitting. You can go back to make changes.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="kpi-card">
                <div className="label-caps">Requestor</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {user.first_name} {user.last_name}
                </div>
              </div>
              <div className="kpi-card">
                <div className="label-caps">Invoice ID</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {setup.invoice_id || "None"}
                </div>
              </div>
              <div className="kpi-card">
                <div className="label-caps">PO Number</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {setup.po_number || "None"}
                </div>
              </div>
              <div className="kpi-card">
                <div className="label-caps">Payment</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {paymentTypes.find((pt) => pt.id === setup.payment_type_id)?.name || "Not set"}
                </div>
              </div>
            </div>

            {setup.invoice_notes && (
              <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm text-slate-600">
                <span className="font-semibold text-slate-700">Notes:</span> {setup.invoice_notes}
              </div>
            )}
          </section>

          {/* Calls table */}
          <section className="table-shell rise-in delay-2">
            <div className="overflow-x-auto">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Property</th>
                    <th>HMC</th>
                    <th>Call Type</th>
                    <th>Service Level</th>
                    <th>Sales Staff</th>
                    <th>Scenario</th>
                    <th>Scheduled</th>
                    <th className="text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {callRows.map((row, idx) => (
                    <tr key={row._key} className="table-row-button">
                      <td className="font-mono text-xs text-slate-400">{idx + 1}</td>
                      <td>
                        <div className="font-semibold text-slate-900 truncate max-w-[14rem]">
                          {row._propertyLabel || "Not set"}
                        </div>
                      </td>
                      <td className="text-sm text-slate-600">{row._hmcLabel || "--"}</td>
                      <td>
                        {callTypes.find((ct) => ct.id === row.call_type_id)?.name || (
                          <span className="text-red-500 text-xs">Required</span>
                        )}
                      </td>
                      <td>
                        {serviceLevels.find((sl) => sl.id === row.service_level_id)?.name || (
                          <span className="text-red-500 text-xs">Required</span>
                        )}
                      </td>
                      <td className="text-sm">{row.sales_staff_email || "--"}</td>
                      <td className="text-sm max-w-[10rem] truncate">{row.scenario || "--"}</td>
                      <td className="text-sm">
                        {row.scheduled_call_date
                          ? new Date(row.scheduled_call_date).toLocaleDateString()
                          : "--"}
                      </td>
                      <td className="text-right font-semibold text-slate-900">
                        {row._cost !== null ? formatCurrency(row._cost) : (
                          <span className="text-xs text-amber-600">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={8} className="text-right font-semibold text-slate-700">
                      Total ({callRows.length} call{callRows.length !== 1 ? "s" : ""})
                    </td>
                    <td className="text-right text-lg font-bold text-slate-950">
                      {formatCurrency(totalCost)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* Validation feedback */}
          {resolvedCount < callRows.length && (
            <section className="rise-in rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
              <strong>{callRows.length - resolvedCount} call{callRows.length - resolvedCount !== 1 ? "s" : ""}</strong>{" "}
              do not have a resolved rate. Select both a call type and service level for each call
              to calculate the cost. You can still submit — the server will calculate final costs.
            </section>
          )}

          {/* Actions */}
          <section className="surface-muted rise-in p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <button className="button-ghost" onClick={goBack}>
                Back to Edit Calls
              </button>
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {callRows.length} call{callRows.length !== 1 ? "s" : ""} &middot;{" "}
                  {formatCurrency(totalCost)}
                </span>
                <button
                  className="button-primary"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? "Submitting..." : "Submit Order"}
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
